import type { IIntegrationDisconnectedNotificationData } from "@frontend-shared/types";
import { Plug } from "lucide-react";
import { memo, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { NotificationCardShell } from "../NotificationCardShell";

interface IIntegrationDisconnectedCardProps {
  uuid: string;
  data: IIntegrationDisconnectedNotificationData;
  createdAt: string;
  isRead: boolean;
  onReconnect: (provider: string, projectUuid: string) => void;
}

const IntegrationDisconnectedCardComponent = ({
  uuid,
  data,
  createdAt,
  isRead,
  onReconnect,
}: IIntegrationDisconnectedCardProps): React.JSX.Element => {
  const { t } = useTranslation("notifications");

  const body = t("type.integration_disconnected.body", {
    providerLabel: data.providerLabel,
    title: data.title,
  });

  // NOTE: Per-card useCallback so the memoized NotificationCardShell doesn't
  // see a fresh function on every parent render. The card forwards both
  // provider AND projectUuid so the parent's handler can switch project
  // BEFORE navigating to /integrations/:provider — integrations are
  // project-scoped, so opening the page in the wrong project would show
  // an empty list.
  const handleReconnect = useCallback((): void => {
    onReconnect(data.provider, data.projectUuid);
  }, [data.projectUuid, data.provider, onReconnect]);

  return (
    <NotificationCardShell
      uuid={uuid}
      variantTestId="integration_disconnected"
      createdAt={createdAt}
      isRead={isRead}
      icon={<Plug className="h-5 w-5 text-slate-500" />}
      iconBgClassName="bg-slate-500/10"
      ctaLabel={t("type.integration_disconnected.cta_label")}
      onCtaClick={handleReconnect}
      body={body}
    />
  );
};

export const IntegrationDisconnectedCard = memo(
  IntegrationDisconnectedCardComponent,
);
